import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { messagesAPI, authAPI } from '../services/api';
import useNotificationPolling from '../hooks/useNotificationPolling';
import { useRealtimeMessages } from '../hooks/useRealtimeMessages';
import HelpGuide from './HelpGuide';
import versionData from '../version.json';
import { useTenantFeatures } from '../context/TenantFeaturesContext';

// Register the inline Swift BadgePlugin once at module load. registerPlugin
// is idempotent for the same name within the same JS context, but calling
// it inside a function that runs every poll triggers Capacitor's
// "already registered" warning on every tick.
const _Badge = registerPlugin('Badge');
// Web has no native Badge plugin — every call becomes a Promise
// rejection ("plugin is not implemented on web") that shows as
// "Uncaught (in promise)" in the console. Wrap the platform check
// once so callers don't have to guard each site (and don't have to
// worry about async rejections escaping try/catch).
const Badge = {
  set: (opts) => Capacitor.isNativePlatform() ? _Badge.set(opts).catch(() => {}) : Promise.resolve(),
  clear: ()   => Capacitor.isNativePlatform() ? _Badge.clear().catch(() => {})    : Promise.resolve(),
};

const Layout = () => {
  const { user, logout, isAdmin, isSuperAdmin, isMasquerading, stopMasquerading } = useAuth();
  const { features } = useTenantFeatures();
  const { branding } = useBranding();

  // Enable global notification polling on all pages
  useNotificationPolling();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [userStatus, setUserStatus] = useState('available');

  // Track network state. Browsers fire 'offline' when the OS reports loss of
  // connectivity (toggled airplane mode, killed Wi-Fi, etc.) and 'online'
  // when it comes back. We surface a banner at the top so the empty
  // conversation list isn't mistaken for "no messages".
  useEffect(() => {
    const setOnline = () => setIsOnline(true);
    const setOffline = () => setIsOnline(false);
    window.addEventListener('online', setOnline);
    window.addEventListener('offline', setOffline);
    return () => {
      window.removeEventListener('online', setOnline);
      window.removeEventListener('offline', setOffline);
    };
  }, []);

  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const statusDropdownRef = useRef(null);
  const isInboundOnly = user?.role === 'inbound_only';

  // Apply dark mode class to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  const statuses = [
    { value: 'available', label: 'Available', color: 'bg-green-500', ringColor: 'ring-green-100', dotColor: 'bg-green-500' },
    { value: 'away', label: 'Away', color: 'bg-yellow-500', ringColor: 'ring-yellow-100', dotColor: 'bg-yellow-500' },
    { value: 'busy', label: 'Busy', color: 'bg-red-500', ringColor: 'ring-red-100', dotColor: 'bg-red-500' },
    { value: 'offline', label: 'Offline', color: 'bg-gray-500', ringColor: 'ring-gray-100', dotColor: 'bg-gray-500' }
  ];

  const navigation = [
    {
      name: 'Dashboard',
      path: '/dashboard',
      icon: (
        <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      )
    },
    {
      name: 'Messaging',
      path: '/messages',
      icon: (
        <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      )
    },
    ...(isAdmin ? [{
      name: 'Queues',
      path: '/queues',
      icon: (
        <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      )
    }] : []),
    // Only show Contacts for non-inbound_only users
    ...(!isInboundOnly ? [{
      name: 'Contacts',
      path: '/contacts',
      icon: (
        <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    }] : []),
    // Only show Templates for non-inbound_only users
    ...(!isInboundOnly ? [{
      name: 'Templates',
      path: '/templates',
      icon: (
        <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    }] : []),
    // Only show Text Blasts for non-inbound_only users
    ...(!isInboundOnly ? [{
      name: 'Text Blasts',
      path: '/text-blasts',
      icon: (
        <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
        </svg>
      )
    }] : []),
    // Only show Reports for non-inbound_only users
    ...(!isInboundOnly ? [{
      name: 'Reports',
      path: '/reports',
      icon: (
        <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    }] : []),
    ...(isAdmin ? [
      {
        name: 'Admin',
        path: '/admin',
        icon: (
          <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        )
      },
      {
        name: 'Billing',
        path: '/billing',
        icon: (
          <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        )
      },
    ] : []),
    ...((user?.role === 'reseller_admin') ? [
      {
        name: 'Resellers',
        path: '/resellers',
        icon: (
          <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        )
      }
    ] : []),
    // Integrations is gated on tenants.enable_integrations (premium
    // feature, off by default) — visible to any tenant admin (admin /
    // super_admin / reseller_admin) so they can manage their own
    // tenant's integrations without needing a super-admin to do it.
    // Toggle the premium flag via super-admin Tenants page.
    ...(isAdmin && features.enable_integrations ? [{
      name: 'Integrations',
      path: '/integrations',
      icon: (
        <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
        </svg>
      )
    }] : []),
    ...(isSuperAdmin ? [
      {
        name: 'Email to Text',
        path: '/email-to-text',
        icon: (
          <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        )
      },
      {
        name: 'Logs',
        path: '/logs',
        icon: (
          <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        )
      }
    ] : [])
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await messagesAPI.getUnreadCount();
      const raw = response?.data;
      // CapacitorHttp on iOS sometimes hands back response.data as a string
      // when Content-Type negotiation falls through; coerce defensively.
      const parsed = typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch { return {}; } })() : (raw || {});
      const count = Number(parsed.unread_count) || 0;
      setUnreadCount(count);
      // Sync iOS home-screen badge to the real-time count via our inline
      // BadgePlugin (Swift). No-op on web — Badge resolves to a stub there.
      try {
        if (count > 0) await Badge.set({ count });
        else await Badge.clear();
      } catch (e) { /* native plugin unavailable, e.g. on web */ }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const fetchUserStatus = async () => {
    try {
      const response = await authAPI.getUserStatus();
      setUserStatus(response.data.currentStatus);
    } catch (error) {
      console.error('Failed to fetch user status:', error);
    }
  };

  const updateUserStatus = async (newStatus) => {
    try {
      await authAPI.updateUserStatus(newStatus);
      setUserStatus(newStatus);
      setShowStatusDropdown(false);
    } catch (error) {
      console.error('Failed to update user status:', error);
    }
  };

  const getInitials = () => {
    if (!user) return '';
    const firstInitial = user.firstName?.charAt(0)?.toUpperCase() || '';
    const lastInitial = user.lastName?.charAt(0)?.toUpperCase() || '';
    return `${firstInitial}${lastInitial}`;
  };

  const getStatusColor = () => {
    return statuses.find(s => s.value === userStatus)?.color || 'bg-green-500';
  };

  // Supabase Realtime: any new message anywhere in the tenant should refresh
  // the in-app/icon badge instantly, regardless of which page the user is on
  // (the Messages page has its own subscription that handles the conversation
  // list — this one only cares about unread totals).
  const onRealtimeMessage = useCallback((message) => {
    // Server filters by tenant via RLS; ignore outbound messages we sent.
    if (message?.direction === 'inbound') {
      fetchUnreadCount();
    }
  }, []);
  const onRealtimeConversationUpdate = useCallback(() => {
    fetchUnreadCount();
  }, []);
  useRealtimeMessages({
    onNewMessage: onRealtimeMessage,
    onConversationUpdate: onRealtimeConversationUpdate
  });

  useEffect(() => {
    // Defensive clear at mount — if fetchUnreadCount fails or is slow, at
    // least the icon doesn't keep showing a stale push count. If there's
    // actual unread, fetchUnreadCount sets the right number a moment later.
    try { Badge.clear(); } catch (e) { /* not native */ }
    fetchUnreadCount();
    fetchUserStatus();
    const interval = setInterval(fetchUnreadCount, 30000); // Poll every 30 seconds
    // capacitor.js dispatches 'app-resumed' when the iOS app foregrounds and
    // 'push-notification' when an APNs push arrives while the app is open.
    // Refresh unread count immediately for both so the in-app badge / icon
    // badge update without waiting for the 30s poll.
    const onResume = () => fetchUnreadCount();
    const onPush = () => fetchUnreadCount();
    // Messages dispatches 'unread-changed' after opening a conversation
    // (which marks its messages read on the server) and after explicit
    // mark-read / mark-unread actions, so the badge updates instantly.
    const onUnreadChanged = () => fetchUnreadCount();
    window.addEventListener('app-resumed', onResume);
    window.addEventListener('push-notification', onPush);
    window.addEventListener('unread-changed', onUnreadChanged);
    return () => {
      clearInterval(interval);
      window.removeEventListener('app-resumed', onResume);
      window.removeEventListener('push-notification', onPush);
      window.removeEventListener('unread-changed', onUnreadChanged);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Offline Banner — appears above everything else when network is down. */}
      {!isOnline && (
        <div
          className="bg-red-600 text-white px-4 py-2 text-center font-medium fixed top-0 left-0 right-0 z-50 flex items-center justify-center gap-2 text-sm"
          style={{ paddingTop: 'calc(0.5rem + env(safe-area-inset-top, 0px))' }}
          role="alert"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636L5.636 18.364m12.728 0L5.636 5.636M12 12h.01" />
          </svg>
          You're offline. Messages and updates will resume when connection is restored.
        </div>
      )}

      {/* Masquerade indicator — compact floating pill overlay so it
          doesn't steal a full row from the layout. Positioned absolute
          in the viewport top-center-ish, above the navbar in z-order. */}
      {isMasquerading && (
        <button
          type="button"
          onClick={stopMasquerading}
          title={`Viewing as ${user?.firstName || ''} ${user?.lastName || ''} — click to exit`}
          className="fixed left-1/2 -translate-x-1/2 z-50 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-600 text-white text-xs font-semibold shadow-lg hover:bg-purple-700 active:bg-purple-800"
          style={{ top: 'calc(env(safe-area-inset-top, 0px) + 4px)' }}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          <span>Viewing as {user?.firstName}</span>
          <span className="pl-1 opacity-80 text-sm leading-none">×</span>
        </button>
      )}

      {/* Top navbar — pad for iOS status bar / dynamic island via safe-area inset */}
      <nav
        className="app-top-nav shadow-sm fixed left-0 right-0 z-30"
        style={{
          backgroundColor: branding.primary_color || '#00658A',
          top: 0,
          paddingTop: 'env(safe-area-inset-top, 0px)'
        }}
      >
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              {branding.logo_url ? (
                <img
                  src={branding.logo_url}
                  alt={branding.app_name || 'Logo'}
                  className="h-10"
                />
              ) : (
                <span className="text-white text-xl font-bold">{branding.app_name || 'SnapText'}</span>
              )}
            </div>
            <div className="flex items-center gap-4">
              {/* System Settings Button (Super Admin Only) */}
              {isSuperAdmin && (
                <Link
                  to="/system-settings"
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                  <span className="hidden sm:inline">System Settings</span>
                </Link>
              )}
              {/* User Avatar with Status Dropdown */}
              <div className="relative" ref={statusDropdownRef}>
                <button
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  className="flex items-center gap-3 px-2 py-2 text-sm hover:bg-white/10 rounded-lg transition-colors"
                >
                  <div className="relative">
                    {/* Avatar square with initials */}
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-semibold text-sm shadow-sm">
                      {getInitials()}
                    </div>
                    {/* Status indicator dot */}
                    <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 ${getStatusColor()} rounded-full border-2 border-white`}></div>
                  </div>
                  <div className="text-left hidden sm:block">
                    <div className="font-medium text-white">
                      {user?.firstName} {user?.lastName}
                    </div>
                    <div className="text-xs text-white/70">
                      {statuses.find(s => s.value === userStatus)?.label || 'Available'}
                    </div>
                  </div>
                  <svg className="w-4 h-4 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showStatusDropdown && (
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                      <div className="text-xs text-gray-500 dark:text-gray-400">Set your status</div>
                    </div>
                    {statuses.map((status) => {
                      const isActive = userStatus === status.value;
                      return (
                        <button
                          key={status.value}
                          onClick={() => updateUserStatus(status.value)}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                            isActive ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                          }`}
                        >
                          <div className="relative flex items-center justify-center w-4 h-4">
                            <div className={`w-3 h-3 rounded-full ${status.dotColor} shadow-sm ${
                              isActive ? 'ring-2 ring-offset-1 ring-offset-white dark:ring-offset-gray-800' : ''
                            } ${
                              status.value === 'available' && isActive ? 'ring-green-200 dark:ring-green-700' :
                              status.value === 'away' && isActive ? 'ring-yellow-200 dark:ring-yellow-700' :
                              status.value === 'busy' && isActive ? 'ring-red-200 dark:ring-red-700' :
                              status.value === 'offline' && isActive ? 'ring-gray-200 dark:ring-gray-600' : ''
                            }`}></div>
                            {isActive && (
                              <div className={`absolute w-3 h-3 rounded-full ${status.dotColor} animate-ping opacity-75`}></div>
                            )}
                          </div>
                          <span className="font-medium text-gray-900 dark:text-gray-100">{status.label}</span>
                          {isActive && (
                            <svg className="w-4 h-4 ml-auto text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                      );
                    })}
                    <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2 px-4 py-1">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Role</div>
                      {user?.role === 'super_admin' && (
                        <span className="inline-block px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">
                          Super Admin
                        </span>
                      )}
                      {user?.role === 'admin' && (
                        <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                          Admin
                        </span>
                      )}
                      {user?.role === 'user' && (
                        <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                          User
                        </span>
                      )}
                    </div>

                    {/* Settings */}
                    <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
                      <Link
                        to="/settings"
                        onClick={() => setShowStatusDropdown(false)}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="font-medium">Settings</span>
                      </Link>
                    </div>

                    {/* Help Guide */}
                    <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
                      <button
                        onClick={() => { setShowStatusDropdown(false); setShowHelp(true); }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                        </svg>
                        <span className="font-medium">Help Guide</span>
                      </button>
                    </div>

                    {/* Dark Mode Toggle */}
                    <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
                      <button
                        onClick={() => setDarkMode(!darkMode)}
                        className="w-full flex items-center justify-between px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {darkMode ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                            </svg>
                          )}
                          <span className="font-medium">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
                        </div>
                        <div className={`w-10 h-6 rounded-full p-1 transition-colors ${darkMode ? 'bg-blue-600' : 'bg-gray-300'}`}>
                          <div className={`w-4 h-4 rounded-full bg-white transition-transform ${darkMode ? 'translate-x-4' : 'translate-x-0'}`}></div>
                        </div>
                      </button>
                    </div>

                    {/* Logout Button */}
                    <div className="border-t border-gray-200 dark:border-gray-700 mt-2">
                      <button
                        onClick={() => {
                          setShowStatusDropdown(false);
                          logout();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        <span className="font-medium">Logout</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div
        className="app-main-wrap flex min-h-[100dvh] md:min-h-screen pb-bottom-nav"
        style={{
          paddingTop: `calc(4rem + env(safe-area-inset-top, 0px))`
        }}
      >
        {/* Left sidebar - Hidden on mobile, shown on desktop */}
        <aside
          className="hidden md:block w-24 bg-white dark:bg-gray-800 shadow-sm border-r border-gray-200 dark:border-gray-700 fixed left-0 bottom-0 overflow-y-auto"
          style={{
            top: `calc(4rem + env(safe-area-inset-top, 0px))`
          }}
        >
          <nav className="px-2 py-3 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`flex flex-col items-center px-2 py-2 rounded-lg text-xs font-medium transition-colors relative ${
                  isActive(item.path)
                    ? 'text-white shadow-sm'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white'
                }`}
                // Active state: solid brand-color pill with white icon+
                // text (icons already stroke="currentColor" so they
                // inherit). The prior 10%-opacity tint was invisible
                // against gray-800 in dark mode and barely legible on
                // white — the solid fill reads at a glance in both
                // themes and matches the pill-tab treatment we use on
                // TenantDetail.
                style={isActive(item.path) ? {
                  backgroundColor: branding.primary_color || '#0066CC'
                } : undefined}
              >
                {item.icon && (
                  <div className="mb-1 relative">
                    {item.icon}
                    {item.path === '/messages' && unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </div>
                )}
                <span className="text-center leading-tight">{item.name}</span>
              </Link>
            ))}
          </nav>
        </aside>

        {/* Main content. `min-w-0` lets the flex item shrink below
            its content's min-width — without it, any wide child
            (long unbreakable string, chart, badge cluster) drags the
            whole page past the viewport and the phone scrolls
            sideways. `overflow-x-hidden` is a belt-and-braces guard
            so a stray wide element clips instead of leaking. */}
        <main className="flex-1 min-w-0 overflow-x-hidden ml-0 md:ml-24 p-0 md:p-6 min-h-full flex flex-col">
          <div className="flex-1"><Outlet /></div>
          <div className="text-center text-[10px] text-gray-400 dark:text-gray-500 mt-6 pb-20 md:pb-2">
            v{versionData.version}
          </div>
        </main>
      </div>

      {/* Bottom Navigation - Mobile only.
          paddingBottom respects the iOS home indicator AND the Android
          system back/home bar on targetSdk 35+ (edge-to-edge default —
          WebView draws full-bleed so we have to inset ourselves). */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 z-40"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="grid grid-cols-5 gap-1 px-2 py-2">
          {(() => {
            // Mobile bottom nav shows 5 slots. The full nav array puts
            // the Admin item at index ~7 (after Templates / Text Blasts
            // / Reports), so slice(0,5) drops it off the phone entirely.
            // For anyone with admin access (isAdmin covers admin /
            // super_admin / reseller_admin), swap it into the last slot
            // so admin-shaped work is reachable without the status
            // dropdown. Non-admins are unaffected.
            let items = navigation.slice(0, 5);
            const admin = navigation.find(i => i.name === 'Admin');
            if (isAdmin && admin && !items.some(i => i.name === 'Admin')) {
              items = [...items.slice(0, 4), admin];
            }
            return items;
          })().map((item) => (
            <Link
              key={item.name}
              to={item.path}
              className={`flex flex-col items-center justify-center py-2 px-1 rounded-lg text-xs font-medium transition-colors relative ${
                isActive(item.path)
                  ? 'text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white'
              }`}
              style={isActive(item.path) ? {
                backgroundColor: branding.primary_color || '#0066CC'
              } : undefined}
            >
              <div className="relative">
                {item.icon}
                {item.path === '/messages' && unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </div>
              <span className="mt-1 text-[10px] leading-tight">{item.name}</span>
            </Link>
          ))}
        </div>
      </nav>

      {/* Floating Help Button — desktop only; mobile uses the profile dropdown */}
      <div className="hidden md:flex fixed bottom-6 right-6 z-40 flex-col items-center">
        <button
          onClick={() => setShowHelp(true)}
          className="w-12 h-12 text-white rounded-full shadow-lg hover:shadow-xl hover:opacity-90 transition-all flex items-center justify-center"
          style={{ backgroundColor: branding.primary_color || '#2563EB' }}
          title="Help Guide"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
          </svg>
        </button>
        <span className="text-[10px] font-medium text-gray-500 mt-1">Help Guide</span>
      </div>

      {/* Help Guide Overlay */}
      <HelpGuide isOpen={showHelp} onClose={() => setShowHelp(false)} />
    </div>
  );
};

export default Layout;
